import React, { useEffect, useRef, useState } from 'react'
import ClipLoader from 'react-spinners/ClipLoader'
import { IoWarningOutline, IoShieldCheckmarkOutline } from 'react-icons/io5'
import './TicketActionModal.css'

const COPY = {
  disable: {
    Icon: IoWarningOutline,
    tone: 'danger',
    title: 'Disable this ticket?',
    lines: [
      'Use this if you think your QR code was copied or shared.',
      'Your current QR code and the PDF we emailed you will stop scanning.',
      'Enable the ticket again at the entrance to get a new QR code in your account. The old QR code and the PDF will not work again.',
    ],
    confirm: 'Disable ticket',
  },
  enable: {
    Icon: IoShieldCheckmarkOutline,
    tone: 'ok',
    title: 'Enable this ticket?',
    lines: [
      'Enable it when you are at the entrance and ready to be scanned.',
      'You will get a fresh QR code. Any older copy stops working for good.',
      'The PDF we emailed you will stop working too. Use the QR code in your account.',
    ],
    confirm: 'Enable ticket',
  },
}

const TicketActionModal = ({ action, ticketNumber, busy, error, onConfirm, onCancel }) => {
  const [password, setPassword] = useState('')
  const inputRef = useRef(null)
  const copy = COPY[action]
  const needsPassword = action === 'enable'

  useEffect(() => {
    if (!action) return
    if (needsPassword && inputRef.current) inputRef.current.focus()
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [action, busy])

  if (!action || !copy) return null

  const { Icon } = copy

  const submit = (e) => {
    e.preventDefault()
    if (busy) return
    if (needsPassword && !password) return
    onConfirm(needsPassword ? password : undefined)
  }

  return (
    <div className="tm-overlay" onClick={() => !busy && onCancel()}>
      <div
        className="tm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className={`tm-icon tm-icon--${copy.tone}`}>
            <Icon size={24} />
          </div>

          <h2 id="tm-title" className="tm-title">
            {copy.title}
          </h2>
          {ticketNumber && <div className="tm-ticket">{ticketNumber}</div>}

          <ul className="tm-body">
            {copy.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          {needsPassword && (
            <div className="tm-field">
              <label htmlFor="tm-password">Enter your password to confirm</label>
              <input
                id="tm-password"
                ref={inputRef}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {error && <div className="tm-error">{error}</div>}

          <div className="tm-actions">
            <button
              type="button"
              className="tm-btn tm-btn--ghost"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`tm-btn tm-btn--${copy.tone}`}
              disabled={busy || (needsPassword && !password)}
            >
              {busy ? <ClipLoader size={14} color="#fff" /> : copy.confirm}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TicketActionModal
